import { useTrans } from "@/translations";

import { useCallback, useMemo } from "react";

import type { Terminal } from "server/dwhApi/terminals/types.js";
import { twMerge } from "tailwind-merge";

type Props = {
  terminals: Terminal[];
  buildingTypesSelected: string[] | null;
  setBuildingTypesSelected: (value: string[] | null) => void;
  onChange: (terminalIds: string[] | null) => void;
};

export const BuildingTypeSelector: React.FC<Props> = ({
  terminals,
  buildingTypesSelected,
  setBuildingTypesSelected,
  onChange,
}) => {
  const { t } = useTrans();
  const allTypes = useMemo(
    () =>
      Array.from(
        new Set(terminals.map((term) => term.commercialTerminalCategory?.name)),
      ),
    [terminals],
  );

  const toggleType = useCallback(
    (type: string) => {
      const nextTypes = buildingTypesSelected
        ? buildingTypesSelected.slice()
        : [];
      const idx = nextTypes.indexOf(type);

      if (idx >= 0) {
        nextTypes.splice(idx, 1);
      } else {
        nextTypes.push(type);
      }

      if (!nextTypes.length) {
        setBuildingTypesSelected(null);
        onChange(null);
        return;
      }

      const updatedTerminals = terminals
        .filter((term) =>
          nextTypes.includes(term.commercialTerminalCategory.name),
        )
        .map((term) => term.terminalId);

      setBuildingTypesSelected(nextTypes);
      onChange(updatedTerminals);
    },
    [buildingTypesSelected, setBuildingTypesSelected, terminals, onChange],
  );

  if (allTypes.length < 2) return null;

  return (
    <div className="border-grey2 border-t px-[13px] py-[12px]">
      <div className="text-grey6 mb-2 text-[10.5px] font-bold tracking-wider uppercase">
        {t("portfolioPage:buildingSelectionBuildingTypes")}
      </div>
      <div className="flex flex-wrap gap-[6px]">
        {allTypes.map((type) => {
          const on = !!buildingTypesSelected?.includes(type);
          return (
            <button
              key={type}
              onClick={() => toggleType(type)}
              className={twMerge(
                "cursor-pointer rounded-full border px-[12px] py-[6px] text-[12px] font-medium transition-colors",
                on
                  ? "border-black bg-black text-white"
                  : "border-grey3 text-black",
              )}
            >
              {t(`portfolioPage:${type}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
};
